import { debug } from '../../utils/debug';
import React, { useState, useEffect } from 'react';
import { m } from 'framer-motion';
import { format, addDays } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { FiArrowLeft, FiMail, FiAlertCircle } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { isDurationValidForStartNow } from '../../utils/openingHours';
import { getLocationOpeningHours } from '../../utils/location';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { useMotionConfig } from '../../hooks/useReducedMotion';
import { getDurationLabel } from '../../utils/locale';

const RentalDuration = ({ onDurationSelect, onBack, merchantConfig = {}, popularDurations, availableDurations = [], bookingData, useAPI = false, locationId = null }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { getMerchantTranslation, getPricingTranslation } = useLanguage();
  const motion = useMotionConfig();

  // Get merchant translations array
  const merchantTranslations = merchantConfig?.translations || [];

  // Get translated custom rental periods text
  const dbCustomMessage = getMerchantTranslation(
    merchantConfig?.customMessage,
    merchantTranslations,
    'custom_rental_periods_text'
  );

  // Check if custom message comes from database (translated) or should use i18n fallback
  const hasCustomMessage = dbCustomMessage && dbCustomMessage.trim() !== '';
  const customMessage = hasCustomMessage ? dbCustomMessage : t('duration.customMessage');
  const supportEmail = merchantConfig?.supportEmail || 'support@example.com';

  debug.log('📅 [RentalDuration] Available durations from pricing rules:', availableDurations);

  // Determine if this is a delivery location (needed for loading state check)
  const isDelivery = bookingData?.location?._apiData?.deliveries || bookingData?.location?.deliveries || false;

  // Check if durations are still loading (empty array on delivery locations means loading)
  const isLoading = availableDurations.length === 0 && isDelivery;

  // Dynamically generate durations from availableDurations prop (from pricing rules)
  // If no availableDurations provided, fall back to a default set.
  //
  // availableDurations items can be either:
  //   - a plain string ID (legacy shape, still produced by default fallback), or
  //   - an object { id, label, labelTranslations } from api/durations.js or
  //     BookingFlow.extractAvailableDurations, carrying the merchant's custom
  //     label (pricing_rules.duration_label) and its translations.
  //
  // For display, we prefer the merchant's custom label (translated via
  // LanguageContext.getPricingTranslation) and fall back to the i18n-generated
  // label from getDurationLabel when no custom label is set.
  const defaultDurations = [
    '2h', '4h', '6h', '9h',
    '1d', '2d', '3d', '4d', '5d', '6d',
    '1w', '8d', '9d', '10d', '2w'
  ];

  // Get opening hours from selected location.
  // Read from _apiData first (in-memory, before sessionStorage round-trip)
  // then fall back to top-level opening_hours (survives sessionStorage).
  const isImmediate = bookingData?.timing === 'immediate';
  const openingHours = getLocationOpeningHours(bookingData?.location);
  const timezone = merchantConfig?.timezone; // Get merchant timezone for opening hours validation

  // Closing periods for the selected location. Used by isDurationValidForStartNow
  // so a "Start now" rental whose return moment lands inside a closure is hidden,
  // mirroring the server-side validation in api/create-booking.js. Only relevant
  // for the immediate flow; reservation durations are validated later in
  // DateSelection/TimeSelection, which fetch their own closing periods.
  const [closingPeriods, setClosingPeriods] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const fetchClosingPeriods = async () => {
      // Only the immediate flow filters durations here, and only API-backed
      // merchants expose a closing-periods endpoint.
      if (!isImmediate || !useAPI || !locationId) {
        if (!cancelled) setClosingPeriods([]);
        return;
      }

      try {
        const today = new Date();
        const startDate = format(today, 'yyyy-MM-dd');
        const endDate = format(addDays(today, 365), 'yyyy-MM-dd');
        const apiUrl = import.meta.env.VITE_API_BASE_URL || '/api';
        const response = await fetch(
          `${apiUrl}/closing-periods?locationId=${locationId}&startDate=${startDate}&endDate=${endDate}`
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        if (!cancelled) setClosingPeriods(result.closingPeriods || []);
      } catch (error) {
        console.error('Error fetching closing periods:', error);
        if (!cancelled) setClosingPeriods([]); // Fallback: no closing-period filtering
      }
    };

    fetchClosingPeriods();
    return () => {
      cancelled = true;
    };
  }, [isImmediate, useAPI, locationId]);

  // Ensure popularDurations is always an array (defensive coding for new merchants)
  const safePDurations = Array.isArray(popularDurations) ? popularDurations : [];

  // Build the displayed duration list inline (no useMemo).
  //
  // Rationale: `isDurationValidForStartNow` is time-sensitive — it compares
  // the rental length against the time remaining until the store closes,
  // using `new Date()` internally. Caching this via useMemo would freeze
  // the filter to the wall-clock at first compute and leave stale options
  // (e.g. a "4 hours" card that would end past closing) visible until an
  // unrelated state change invalidates the memo. The computation is cheap
  // (filter + map over a small array), so we recompute on every render.
  // This also keeps the per-entry `displayLabel` reactive to language
  // switches without a dependency list.
  const rawDurations = availableDurations.length > 0 ? availableDurations : defaultDurations;

  const durations = rawDurations
    .map(entry => {
      // Normalize legacy string shape to object shape
      const durationObj = typeof entry === 'string'
        ? { id: entry, label: null, labelTranslations: [], durationType: null }
        : {
            id: entry.id,
            label: entry.label || null,
            labelTranslations: Array.isArray(entry.labelTranslations) ? entry.labelTranslations : [],
            durationType: entry.durationType || null,
          };

      // Resolve current-language custom label (empty string if none set).
      // getPricingTranslation handles the language chain:
      //   current language translation → default language main value → ''
      const resolvedCustomLabel = getPricingTranslation(
        durationObj.label,
        durationObj.labelTranslations
      );

      return {
        ...durationObj,
        // Display label used by the grid below
        displayLabel: getDurationLabel(durationObj.id, t, resolvedCustomLabel),
      };
    })
    .filter(d => {
      // For "Start now", check if duration is valid given current time and
      // closing hours. For "Make reservation", all durations are available
      // (the check moves to TimeSelection once the user picks a start time).
      if (isImmediate && openingHours) {
        const isValid = isDurationValidForStartNow(d.id, openingHours, timezone, closingPeriods, d.durationType);
        if (!isValid) {
          debug.log(`📅 [RentalDuration] Filtering out ${d.id} - not valid for immediate start`);
        }
        return isValid;
      }
      return true;
    })
    .map(d => ({
      ...d,
      popular: safePDurations.includes(d.id),
    }));

  // Determine back button text based on flow type
  const hasDeliveryArea = bookingData?.deliveryArea;
  let backButtonText, backAriaLabel;

  if (hasDeliveryArea) {
    // DELIVERY_AREAS flow: has delivery area selection step
    backButtonText = t('duration.backToDelivery');
    backAriaLabel = "Back to delivery area selection";
  } else if (isDelivery) {
    // DELIVERY_DISTANCE flow: no delivery area step, goes directly from locations
    backButtonText = t('common.back');
    backAriaLabel = "Back to location selection";
  } else {
    // Pickup flow: has timing selection step
    backButtonText = t('duration.backToTiming');
    backAriaLabel = "Back to timing selection";
  }

  return (
    <div className="max-w-4xl mx-auto">
      <button
        onClick={onBack}
        className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        aria-label={backAriaLabel}
      >
        <SafeIcon icon={FiArrowLeft} className="w-5 h-5" aria-hidden="true" />
        <span>{backButtonText}</span>
      </button>
      <m.div
        {...motion.fadeInUp}
        className="text-center mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('duration.title')}</h1>
        <p className="text-gray-600">
          {isImmediate
            ? t('duration.selectDurationImmediate')
            : t('duration.selectDuration')}
        </p>
      </m.div>

      {/* Show loading state while durations are being fetched */}
      {isLoading ? (
        <m.div
          {...motion.fadeIn}
          className="flex flex-col items-center justify-center py-16"
        >
          <div
            className="animate-spin rounded-full h-12 w-12 border-b-2 mb-4"
            style={{ borderColor: theme.colors.primary.base }}
          ></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </m.div>
      ) : durations.length === 0 && isImmediate ? (
        /* Show error message if no durations available for "Start now" */
        <m.div
          {...motion.fadeInUp}
          className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-8 text-center"
        >
          <SafeIcon icon={FiAlertCircle} className="w-16 h-16 text-yellow-600 mx-auto mb-4" aria-hidden="true" />
          <h2 className="text-xl font-bold text-gray-900 mb-3">{t('duration.storeClosed')}</h2>
          <p className="text-gray-700 mb-2">
            {t('duration.storeClosedMessage')}
          </p>
          <p className="text-gray-600 text-sm mb-6">
            {t('duration.useReservation')}
          </p>
          <button
            onClick={onBack}
            className="px-6 py-3 rounded-lg font-semibold transition-all duration-200"
            style={{ background: theme.colors.primary.gradient, color: theme.colors.primary.contrastText }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            aria-label={t('duration.goBackMakeReservation')}
          >
            {t('duration.goBackMakeReservation')}
          </button>
        </m.div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {durations.map((duration, index) => {
              // Create descriptive aria-label for screen readers
              const ariaLabel = `${duration.displayLabel}${duration.popular ? ', Popular choice' : ''}`;

              // Persist the raw label + translations on the booking so that
              // downstream steps (ProductSelection, Checkout, BikeDetailModal)
              // can resolve the current-language label without re-fetching.
              // `label` keeps the default-language merchant label (may be null);
              // getDurationLabel falls back to i18n when it is null.
              const onSelectPayload = {
                id: duration.id,
                label: duration.label,
                labelTranslations: duration.labelTranslations,
                durationType: duration.durationType || null,
              };

              return (
              <m.button
                key={duration.id}
                {...motion.stagger(index, 0.05)}
                {...motion.noHover}
                onClick={() => onDurationSelect(onSelectPayload)}
                aria-label={ariaLabel}
                className="duration-card w-full relative"
              >
                {duration.popular && (
                  <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-gradient-to-r from-orange-400 to-red-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      {t('duration.popular')}
                    </span>
                  </div>
                )}
                <div
                  className={`bg-white rounded-xl shadow-md border-2 transition-all duration-300 hover:shadow-lg ${
                    duration.popular ? 'border-orange-400 ring-2 ring-orange-100' : 'border-gray-200 hover:border-blue-400'
                  }`}
                >
                  <div className="p-4 text-center">
                    <h2 className="font-bold text-gray-900 mb-1">{duration.displayLabel}</h2>
                  </div>
                </div>
              </m.button>
              );
            })}
          </div>
          <div className="mt-8 p-4 bg-blue-50 rounded-xl">
            <div className="flex items-center space-x-2 text-blue-800">
              <SafeIcon icon={FiMail} className="w-5 h-5" />
              <span className="font-medium">{t('duration.customPeriods')}</span>
            </div>
            <p className="text-blue-700 text-sm mt-1">
              {hasCustomMessage ? (
                // Database custom message - use as-is (may already include contact info)
                customMessage
              ) : (
                // Fallback translation - append email
                <>
                  {customMessage} (
                  <a
                    href={`mailto:${supportEmail}`}
                    className="underline hover:text-blue-900 transition-colors"
                  >
                    {supportEmail}
                  </a>
                  ).
                </>
              )}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default RentalDuration;